import { TelnyxRTC } from "@telnyx/webrtc";
import { api, type MonitorMode } from "./api";
import { applySpeaker, audioConstraints, getDevicePrefs, onDevicePrefsChange, preferredCodecs, setDevicePrefs, isIOS, micIsOn, micOpensSilently } from "./devices";

/** Live connection quality of the current call (from WebRTC stats). */
/** Wi-Fi voice marking (DSCP) on our call audio. Off: see prioritizeAudio. */
const PRIORITIZE_AUDIO = false;

/** The last call's audio path on this device: Telnyx media server address, region, delay. */
export interface MediaPath { ip: string; protocol: string; localType: string; remoteType: string; region: string; rttMs: number | null; relay: CallQuality["relay"]; at: number; bufferMs?: number | null }
const PATH_KEY = "joy.mediaPath.v1";
function saveMediaPath(p: MediaPath) {
  try {
    localStorage.setItem(PATH_KEY, JSON.stringify(p));
  } catch {
    // private mode
  }
}
export function lastMediaPath(): MediaPath | null {
  try {
    const p = JSON.parse(localStorage.getItem(PATH_KEY) ?? "null");
    return p && typeof p.ip === "string" ? (p as MediaPath) : null;
  } catch {
    return null;
  }
}

/** What happened to a call's sound, measured on this device (sent to the server when the call ends). */
export interface AudioReport {
  callId: string;
  direction: "inbound" | "outbound";
  startedAt: number;
  endedAt: number;
  codec: string;
  /** 1.10.27: the format this device sends (codec: the one it hears). */
  sendCodec?: string;
  relay: null | "udp" | "tcp" | "tls";
  localType: string;
  network: string;
  jitterMaxMs: number;
  rttMaxMs: number;
  loss: number | null;
  farLostMax: number | null;
  /** Incoming audio made up by the phone because packets were missing or late (not counting silence). */
  concealed: number;
  total: number;
  concealEvents: number;
  inserted: number;
  removed: number;
  /** Speaker output the phone had to make up (its own audio couldn't keep up), seconds. */
  synthSecs: number;
  playoutSecs: number;
  samples: number;
  /** Jitter protection: the largest extra buffer this call needed, ms (0: never needed; null: not supported on this phone). */
  jbTargetMaxMs: number | null;
}

export interface CallQuality {
  /** Round trip to Telnyx's media server, ms. */
  rttMs: number | null;
  /** Incoming audio jitter, ms. */
  jitterMs: number | null;
  /** Share of incoming audio packets lost since the call started (0–1). */
  loss: number | null;
  /** Audio goes through a TURN relay (slower); "tcp"/"tls" relays are the slowest. */
  relay: null | "udp" | "tcp" | "tls";
  /** How much incoming audio this phone is holding before playing it (the browser's jitter buffer), ms. */
  bufferMs: number | null;
  grade: "good" | "fair" | "poor" | "unknown";
  /**
   * The first seconds after the call connected: the longest stretch with no audio arriving from the other side
   * (ms), and how long until their audio started. Shows whether the start of the call got clipped.
   */
  startGapMs?: number | null;
  startAudioMs?: number | null;
}
import { playCallWaitingBeep } from "./sounds";
import { recordCallRtt, recordCallDelay, effectiveRegion, summarize, type SpeedResult } from "./region";

/** The speed test's silent test call (the server answers it; see SPEED_TEST_NUMBER in callflow.ts). */
export const SPEED_TEST_NUMBER = "+10000000000";

// The Telnyx browser SDK's call object (loosely typed: we only use a few fields).
/** A speed test call (to +1 000 000 0000), with or without the "+", whatever its direction says so far. */
function isSpeedTestCall(c: { options?: { destinationNumber?: string } }): boolean {
  return String(c.options?.destinationNumber ?? "").replace(/^\+/, "") === SPEED_TEST_NUMBER.replace(/^\+/, "");
}

export interface PhoneCall {
  id: string;
  state: string; // new, requesting, trying, early, ringing, answering, active, held, hangup, destroy
  direction: "inbound" | "outbound";
  options: {
    destinationNumber?: string;
    remoteCallerNumber?: string;
    remoteCallerName?: string;
    telnyxSessionId?: string;
    telnyxCallControlId?: string;
    /** SIP X- headers on an incoming call (Telnyx passes them through as dialogParams.custom_headers). */
    customHeaders?: { name: string; value: string }[];
    /** Audio sent to the other side (SDK: used instead of asking for the mic when set). */
    localStream?: MediaStream;
    /** Where the SDK plays this call's audio (an element or its id). Read when its audio arrives. */
    remoteElement?: string | HTMLMediaElement;
  };
  /** Set by Phone: started without the mic (iPhone, Mic switch off). The other side hears silence until you turn it on. */
  micOff?: boolean;
  /** The other side's audio (SDK getter). */
  readonly remoteStream?: MediaStream;
  readonly isAudioMuted?: boolean;
  answer(params?: { customHeaders?: { name: string; value: string }[] }): Promise<void> | void;
  hangup(): Promise<void> | void;
  hold(): Promise<unknown>;
  unhold(): Promise<unknown>;
  muteAudio(): void;
  unmuteAudio(): void;
  dtmf(digits: string): void;
  /** Switch microphone mid-call (SDK). */
  setAudioInDevice?(deviceId: string, muted?: boolean): Promise<void>;
  /** Switch speaker mid-call (SDK; needs setSinkId support). */
  setAudioOutDevice?(deviceId: string): Promise<boolean>;
  /** Set by Phone: this leg is an admin listening in (auto-answered, no ringtone). */
  isMonitor?: boolean;
  /** Set by Phone for monitor legs: from the X-Joy-Monitor header, or the mode asked for in listenIn/expectMonitor. */
  monitorMode?: MonitorMode;
  /** Set by Phone when it answered this call by itself (pickup or monitor): don't ring for it. */
  autoAnswered?: boolean;
  /** A call with a teammate (app to app, not a customer): their name. */
  intercomName?: string;
  /** 7.10.30: set by the app: you tapped Mute on this call (your own choice; hold and swap never change it). */
  userMuted?: boolean;
  /** 7.10.30: set by the app: on hold through the server (Hold button, transfer): your mic stays off until Resume. */
  serverHeld?: boolean;
}

/** Teammate calls: the app dials +1000000 and the teammate's id (4 digits); the server connects their app. */
export const intercomNumber = (userId: number) => `+1000000${String(userId).padStart(4, "0")}`;

const MONITOR_HEADER = "x-joy-monitor";
const MONITOR_MODES: readonly MonitorMode[] = ["listen", "whisper", "barge"];
/** How often the soft call-waiting beep repeats while a second call is waiting. */
const WAITING_BEEP_EVERY = 4_000;

const ENDED = new Set(["hangup", "destroy", "purge"]);
export const isEnded = (c: PhoneCall) => ENDED.has(c.state);
export const otherParty = (c: PhoneCall) =>
  asE164((c.direction === "inbound" ? c.options.remoteCallerNumber : c.options.destinationNumber) ?? "");
/**
 * 4.10.21 "a saved contact is still coming up as the phone number": an international caller reaches the app as bare
 * digits ("61412109137", no "+"), while contacts are saved as "+61412109137", so the name wasn't found. Bare digits
 * become E.164 (10 digits: a US number; 11–15: the country code is already there). Names, "anonymous", short codes
 * and anything with a "+" stay as they are.
 */
export function asE164(n: string): string {
  const t = n.trim();
  if (!/^\d{10,15}$/.test(t)) return t;
  return t.length === 10 ? `+1${t}` : `+${t}`;
}

type Listener = () => void;

/** Waits between reconnect attempts after the line drops: 2s, 5s, 15s, 30s, then every minute. */
const RECONNECT_DELAYS = [2_000, 5_000, 15_000, 30_000];
const RECONNECT_EVERY = 60_000;
/** Login tokens last 24 hours; sign in again with a fresh one well before that. */
const TOKEN_REFRESH_MS = 20 * 60 * 60 * 1000;

/**
 * One connection to Telnyx per open app. Calls ring here while the app is open;
 * when it's closed, the server's push alert brings the user back to answer.
 *
 * Calls: `call` is the call on screen (active, ringing, or being dialed). With call waiting,
 * `waiting` is a second incoming call ringing meanwhile, and `held` is the call put on hold
 * to take it (swap() switches between them).
 */
export class Phone {
  private client: TelnyxRTC | null = null;
  private listeners = new Set<Listener>();
  private sipUsername = "";
  private connecting: Promise<void> | null = null;
  ready = false;
  /** The active / primary call. */
  call: PhoneCall | null = null;
  /** Call waiting: another call ringing while you're on one. */
  waiting: PhoneCall | null = null;
  /** Call waiting: the call on hold (SDK hold) while you talk on `call`. */
  held: PhoneCall | null = null;
  /** When the active call ends with another on hold: take it off hold automatically (else it stays held until swap()). */
  autoResumeHeld = true;
  /** Plays a soft repeating beep while a call is waiting (set false if the UI does its own). */
  waitingBeep = true;
  /** Set when the user tapped Answer on a call that hadn't reached this phone yet. */
  private autoAnswer: { sessionId: string; until: number } | null = null;
  /** The next inbound call within this window is a monitor leg: answered silently. */
  private monitorExpect: { until: number; mode: MonitorMode } | null = null;
  /** An admin listening in on a teammate's call (that leg is auto-answered and shown as "Listening in"). */
  monitoring: { callId: string; mode: MonitorMode } | null = null;
  private monitorTimer: ReturnType<typeof setTimeout> | undefined;
  /** True while a call is being answered automatically (no ringtone for it). */
  get autoAnswering() {
    const now = Date.now();
    // 1.10.28: only the call being picked up / listened in to rings silently. Before, any call arriving within 20 s
    // of an Answer tap whose call never came here (or a listen-in that failed) rang with no ringtone at all.
    const sid = this.call?.options.telnyxSessionId;
    return (
      (!!this.autoAnswer && now < this.autoAnswer.until && (!sid || sid === this.autoAnswer.sessionId)) ||
      (!!this.monitorExpect && now < this.monitorExpect.until && !this.call) ||
      !!this.call?.autoAnswered ||
      !!this.call?.isMonitor
    );
  }
  /** True after disconnect() (signed out): no automatic reconnects. */
  private stopped = true;
  /** The Telnyx region this connection uses ("" = Telnyx default routing). */
  region = "";
  private lastRttSave = 0;
  /** Jitter buffer totals at the last reading, to work out the current buffer (not the whole-call average). */
  private lastBuf: { delay: number; count: number; callId?: string } | null = null;
  /** Clipped-start check: the call being watched, and what it found (see watchStart). */
  private startWatched: string | null = null;
  /** Told when the first seconds of a call came with a gap (so testing shows it without digging). */
  onStartGap?: (ms: number) => void;
  private startReport: { callId: string; gapMs: number | null; firstMs: number | null } | null = null;

  /**
   * For the first 5 seconds after a call connects, checks every 50 ms whether audio from the other side is
   * arriving. Records the longest stretch with nothing arriving and when their audio first came, so a clipped
   * start can be seen in the call's connection details.
   */
  // ---- call trace (diagnostics): states, audio packets and the SDK's own timing marks, sent to the server ----
  private trace: { id: string; started: number; events: { at: number; what: string }[]; timer?: number; stopAt?: number; c: PhoneCall } | null = null;
  private traceEvent(what: string) {
    if (this.trace && this.trace.events.length < 300) this.trace.events.push({ at: Date.now(), what });
  }
  /** Notes something from outside (e.g. "server says they answered"); stops tracing 10 s later. */
  traceMark(what: string) {
    this.traceEvent(what);
    if (this.trace && /answered/.test(what)) this.trace.stopAt = Date.now() + 10_000;
  }
  private traceStart(c: PhoneCall) {
    this.traceStop();
    const t = { id: c.id, started: Date.now(), events: [] as { at: number; what: string }[], c, timer: undefined as number | undefined, stopAt: undefined as number | undefined };
    this.trace = t;
    this.traceEvent(`${c.direction} call, state ${c.state}`);
    let lastPackets = -1, lastChange = Date.now(), flowing = false, heard = false;
    t.timer = window.setInterval(async () => {
      if (this.trace !== t) return;
      if (isEnded(c) || (t.stopAt && Date.now() > t.stopAt) || Date.now() - t.started > 120_000) return void this.traceStop();
      const pc = (c as unknown as { peer?: { instance?: RTCPeerConnection } }).peer?.instance;
      if (!pc) return;
      try {
        const stats = await pc.getStats();
        let packets = 0, level = 0, pair = "";
        stats.forEach((r: any) => {
          if (r.type === "inbound-rtp" && r.kind === "audio") {
            packets += r.packetsReceived ?? 0;
            level = Math.max(level, r.audioLevel ?? 0);
          }
          if (r.type === "candidate-pair" && (r.nominated || r.selected) && r.state === "succeeded") pair = r.id;
        });
        const now = Date.now();
        if (packets !== lastPackets) {
          if (!flowing) this.traceEvent(lastPackets < 0 ? `audio packets: ${packets}` : `audio packets arriving again after ${now - lastChange} ms (total ${packets})`);
          flowing = true;
          lastPackets = packets;
          lastChange = now;
        } else if (flowing && now - lastChange > 300) {
          flowing = false;
          this.traceEvent(`audio packets stopped (total ${packets})`);
        }
        if (!heard && level > 0.02) {
          heard = true;
          this.traceEvent(`first sound from the other side (level ${level.toFixed(2)})`);
        }
        if (pair && !t.events.some((e) => e.what.startsWith("connected path"))) this.traceEvent(`connected path ${pair}`);
      } catch {
        // stats not available
      }
    }, 100);
  }
  /**
   * 4.10.21: what happens to a call after its first seconds (the trace above stops then): a second call arriving,
   * and how it ended (Telnyx's cause and SIP code), sent to /api/diag/calls when it ends. So a dropped call can be
   * measured, not guessed.
   */
  private late = new Map<string, { at: number; what: string }[]>();
  private lateNote(c: PhoneCall, what: string) {
    const l = this.late.get(c.id) ?? [];
    if (l.length < 40) l.push({ at: Date.now(), what });
    this.late.set(c.id, l);
  }
  private endReported = new Set<string>();
  private reportEnd(c: PhoneCall) {
    if (this.endReported.has(c.id)) return;
    this.endReported.add(c.id);
    if (c.isMonitor || this.testCallIds.has(c.id) || !this.talked.has(c.id) && !this.late.has(c.id)) return void this.late.delete(c.id);
    const x = c as unknown as { cause?: string; causeCode?: number; sipCode?: number; sipReason?: string };
    this.lateNote(c, `ended: ${[x.cause, x.causeCode, x.sipCode, x.sipReason].filter((v) => v != null && v !== "").join(" ") || "no cause given"}${this.theirBye.has(c.id) ? " (the other side hung up)" : ""}${document.visibilityState !== "visible" ? " (app in the background)" : ""}`);
    const events = this.late.get(c.id)!;
    this.late.delete(c.id);
    const o = c.options as { telnyxSessionId?: string; telnyxCallControlId?: string };
    void api.diagCall({
      sessionId: o.telnyxSessionId ?? null, callControlId: o.telnyxCallControlId ?? null, direction: c.direction,
      region: this.region || "default", codec: getDevicePrefs().codec, userAgent: navigator.userAgent.slice(0, 160), events,
    }).catch(() => {});
  }
  private traceStop() {
    const t = this.trace;
    if (!t) return;
    this.trace = null;
    clearInterval(t.timer);
    // The calling library's own timing marks for this call (invite sent, ringing, media, answer…).
    try {
      for (const m of performance.getEntriesByType("mark")) {
        if (m.name.includes(t.id)) t.events.push({ at: Math.round(performance.timeOrigin + m.startTime), what: `sdk ${m.name.replace(t.id, "").replace(/^[-_:]+/, "")}` });
      }
    } catch {
      // no marks
    }
    t.events.sort((a, b) => a.at - b.at);
    const o = t.c.options as { telnyxSessionId?: string; telnyxCallControlId?: string; destinationNumber?: string };
    void api.diagCall({
      sessionId: o.telnyxSessionId ?? null, callControlId: o.telnyxCallControlId ?? null, direction: t.c.direction,
      region: this.region || "default", codec: getDevicePrefs().codec, userAgent: navigator.userAgent.slice(0, 160), events: t.events,
    }).catch(() => {});
  }

  /**
   * Outgoing calls: our side connects while it rings, and nothing arrives until the other side answers. The first
   * audio packet IS their answer, so watch for it every 20 ms and say so at once (the ring tone stops on it,
   * instead of ~0.2 s later when the server's notice arrives, which covered their first word).
   */
  onFarAudio?: (callId: string) => void;
  private farWatched: string | null = null;
  private watchFarAudio(c: PhoneCall) {
    if (this.farWatched === c.id) return;
    this.farWatched = c.id;
    const started = Date.now();
    let base = -1;
    const tick = async () => {
      if (this.call?.id !== c.id || isEnded(c) || Date.now() - started > 120_000) return;
      const pc = (c as unknown as { peer?: { instance?: RTCPeerConnection } }).peer?.instance;
      let packets = 0;
      try {
        const stats = pc ? await pc.getStats() : null;
        stats?.forEach((r: any) => {
          if (r.type === "inbound-rtp" && r.kind === "audio") packets += r.packetsReceived ?? 0;
        });
      } catch {
        // try again
      }
      if (base < 0) base = packets;
      if (packets > base) return void this.onFarAudio?.(c.id);
      setTimeout(() => void tick(), 20);
    };
    void tick();
  }

  /** Outgoing calls: start the clipped-start check now (the other side just answered). */
  watchStartNow() {
    const c = this.call;
    if (!c || isEnded(c) || this.startWatched === c.id) return;
    this.startWatched = c.id;
    void this.watchStart(c);
  }

  private async watchStart(c: PhoneCall) {
    const pc = (c as unknown as { peer?: { instance?: RTCPeerConnection } }).peer?.instance;
    if (!pc) return;
    const t0 = performance.now();
    let lastPackets = -1, lastChange = t0, longest = 0, first: number | null = null;
    while (performance.now() - t0 < 5000 && this.call?.id === c.id && !isEnded(c)) {
      try {
        const stats = await pc.getStats();
        let packets = 0;
        stats.forEach((r: any) => {
          if (r.type === "inbound-rtp" && r.kind === "audio") packets += r.packetsReceived ?? 0;
        });
        const now = performance.now();
        if (packets !== lastPackets) {
          if (lastPackets >= 0) {
            if (first === null) first = Math.round(now - t0);
            longest = Math.max(longest, now - lastChange);
          }
          lastPackets = packets;
          lastChange = now;
        }
      } catch {
        return;
      }
      await new Promise((r) => setTimeout(r, 50));
    }
    longest = Math.max(longest, performance.now() - lastChange);
    this.startReport = { callId: c.id, gapMs: Math.round(longest), firstMs: first };
    if (longest > 500) {
      console.warn(`[voice] start of call: no audio for ${Math.round(longest)} ms (first audio after ${first ?? "?"} ms)`);
      this.onStartGap?.(Math.round(longest));
    }
  }

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  /** The line needs a fresh connection, but a call is in progress: do it when the call ends. */
  private reconnectAfterCall = false;
  private waitingBeepTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    if (typeof window !== "undefined") {
      window.addEventListener("online", () => {
        if (this.stopped || this.ready) return;
        this.clearReconnectTimer();
        this.reconnectAttempt = 0;
        this.connect().catch(() => {});
      });
      // Settings changed elsewhere (settings screen): use them for the next call, and the speaker right away.
      onDevicePrefsChange(() => {
        void this.applyAudioSettings();
        void this.applySpeakerNow();
        // A new calling region needs a fresh connection (waits for any call to end first).
        this.useBestRegion();
      });
    }
  }

  /** Reconnects through the region calls should use now (after a speed test or a settings change), if it changed. */
  useBestRegion() {
    if (this.regionOverride != null || !this.client || this.stopped) return;
    if (effectiveRegion(getDevicePrefs()) === this.region) return;
    if (this.busy()) this.reconnectAfterCall = true;
    else this.reconnectNow();
  }

  /** Fresh phone line connection (e.g. this login moved to another media site); after the current call, if any. */
  reconnectLine() {
    if (this.stopped || !this.client) return;
    this.token = null; // the login moved: start from a fresh one
    if (this.busy()) this.reconnectAfterCall = true;
    else this.reconnectNow();
  }

  /** True while any call (or a speed test call) is in progress here. */
  isBusy() {
    return this.busy() || this.testing;
  }

  // ------------------------------------------------------------ speed test

  /** Set during a speed test: connect through this region instead of the usual one. */
  private regionOverride: string | null = null;
  private testCallIds = new Set<string>();
  private testing = false;

  /**
   * One silent test call through `region` (the server answers it, nobody rings). Measures the real round trip
   * of call audio between this device and Telnyx's media server for ~6 s, then hangs up.
   * Throws if a real call is in progress or comes in meanwhile (that call always wins).
   */
  /** One test call; if it's turned down right away (a hiccup while the line switches regions), tries once more. */
  async testCall(region: string, site: string | null, measureMs = 6000): Promise<SpeedResult> {
    const first = await this.testCallOnce(region, site, measureMs);
    if (!first.error?.startsWith("Ended right away") && !/didn't connect|Couldn't connect/.test(first.error ?? "")) return first;
    if (this.busy()) return first;
    await new Promise((r) => setTimeout(r, 1500));
    const again = await this.testCallOnce(region, site, measureMs);
    return again.error ? { ...again, error: `${again.error}; first try: ${first.error}` } : again;
  }

  private async testCallOnce(region: string, site: string | null, measureMs = 6000): Promise<SpeedResult> {
    if (this.busy()) throw new Error("You're on a call");
    this.testing = true;
    const base: SpeedResult = { region, site, ms: null, spread: null, samples: 0, ip: null, relay: null };
    let ctx: AudioContext | null = null;
    let call: PhoneCall | null = null;
    try {
      this.regionOverride = region;
      if (this.region !== region || !this.ready || !this.client) {
        this.clearReconnectTimer();
        this.ready = false;
        await this.connecting?.catch(() => {});
        await this.connect();
      }
      if (!this.client || this.region !== region) throw new Error("Couldn't connect through this region");
      if (this.busy()) throw new Error("A call came in");
      // Silence instead of the mic: no mic light, nothing recorded, and it works without mic permission.
      ctx = new AudioContext();
      const silent = ctx.createMediaStreamDestination();
      // 1.10.28, measured: an empty stand-in sent no audio at all (0 packets in 3 s), so the far side's delay reports
      // never came and the test leaned on connection checks alone. Real silence flows (149 packets, delay reported).
      // Only for this test: during real calls the silence stays paused (it makes iPhone call audio crackle).
      try {
        const zero = ctx.createConstantSource();
        zero.offset.value = 0;
        zero.connect(silent);
        zero.start();
      } catch {
        // older browser: the empty stand-in, as before
      }
      call = this.client.newCall({
        destinationNumber: SPEED_TEST_NUMBER,
        callerNumber: this.sipUsername,
        callerName: "Speed test",
        audio: true,
        video: false,
        localStream: silent.stream,
        ...(preferredCodecs() ? { preferred_codecs: preferredCodecs() } : {}),
      } as never) as unknown as PhoneCall;
      this.testCallIds.add(call.id);
      const c = call;
      const pcOf = () => (c as unknown as { peer?: { instance?: RTCPeerConnection } }).peer?.instance;
      // Wait for the server to answer (up to 8 s).
      const t0 = Date.now();
      while (c.state !== "active" && !isEnded(c) && Date.now() - t0 < 8000) {
        if (this.busy()) throw new Error("A call came in");
        await new Promise((r) => setTimeout(r, 100));
      }
      if (c.state !== "active") {
        // Say exactly why (the phone network's own reason), so a failed test can be fixed instead of guessed at.
        const x = c as unknown as { cause?: string; causeCode?: number; sipCode?: number; sipReason?: string };
        const why = [x.sipCode && `${x.sipCode}${x.sipReason ? ` ${x.sipReason}` : ""}`, x.cause].filter(Boolean).join(", ");
        throw new Error(isEnded(c)
          ? `${Date.now() - t0 < 2500 ? "Ended right away" : "Ended before it connected"}${why ? ` (${why})` : ""}`
          : "The test call didn't connect (no answer in 8 s)");
      }
      // Each new STUN answer on the connected path is one round-trip reading (the browser checks the path every
      // ~0.5 s at first, later every ~2.5 s). RTCP reports add more readings.
      const samples: number[] = [];
      let lastTotal = 0, lastResp = 0, lastRtcp = 0;
      const until = Date.now() + measureMs;
      while (Date.now() < until && !isEnded(c)) {
        if (this.busy()) throw new Error("A call came in");
        const pc = pcOf();
        if (pc) {
          try {
            const stats = await pc.getStats();
            const byId = new Map<string, any>();
            stats.forEach((r: any) => byId.set(r.id, r));
            stats.forEach((r: any) => {
              if (r.type === "candidate-pair" && (r.nominated || r.selected) && r.state === "succeeded") {
                const resp = r.responsesReceived ?? 0, total = r.totalRoundTripTime ?? 0;
                if (resp > lastResp && total > lastTotal) {
                  const each = ((total - lastTotal) / (resp - lastResp)) * 1000;
                  if (lastResp > 0 || resp <= 3) samples.push(each);
                }
                lastResp = resp;
                lastTotal = total;
                const local = byId.get(r.localCandidateId), remote = byId.get(r.remoteCandidateId);
                base.ip = remote?.address ?? remote?.ip ?? base.ip;
                base.relay = local?.candidateType === "relay" ? String(local.relayProtocol ?? "udp") : null;
              }
              if (r.type === "remote-inbound-rtp" && r.kind === "audio" && typeof r.roundTripTime === "number" && (r.roundTripTimeMeasurements ?? 0) > lastRtcp) {
                lastRtcp = r.roundTripTimeMeasurements;
                samples.push(r.roundTripTime * 1000);
              }
            });
          } catch {
            // stats not ready yet
          }
        }
        await new Promise((r) => setTimeout(r, 200));
      }
      const sum = summarize(samples.filter((x) => x > 0 && x < 3000));
      return { ...base, ...sum, samples: samples.length };
    } catch (e) {
      // Tell /api/diag/calls why this test call failed (next to the server's own steps for it, when it got there).
      const o = (call?.options ?? {}) as { telnyxSessionId?: string; telnyxCallControlId?: string };
      const x = (call ?? {}) as { cause?: string; causeCode?: number; sipCode?: number; sipReason?: string; state?: string };
      void api.diagCall({
        kind: "speed test", sessionId: o.telnyxSessionId ?? null, callControlId: o.telnyxCallControlId ?? null, direction: "outbound",
        region: region || "default", site, error: (e as Error).message, state: x.state ?? null, cause: x.cause ?? null, causeCode: x.causeCode ?? null,
        sipCode: x.sipCode ?? null, sipReason: x.sipReason ?? null, lineReady: this.ready, userAgent: navigator.userAgent.slice(0, 160),
        events: [{ at: Date.now(), what: `speed test failed: ${(e as Error).message}` }],
      }).catch(() => {});
      return { ...base, error: (e as Error).message };
    } finally {
      if (call && !isEnded(call)) await Promise.resolve(call.hangup()).catch(() => {});
      void ctx?.close().catch(() => {});
      this.testing = false;
    }
  }

  /** After a speed test: back to the region calls should use (the new best, if it changed). */
  endSpeedTest() {
    this.regionOverride = null;
    this.testing = false;
    if (!this.client || this.stopped) return;
    if (effectiveRegion(getDevicePrefs()) !== this.region) {
      if (this.busy()) this.reconnectAfterCall = true;
      else this.reconnectNow();
    }
  }

  private hasActiveCall() {
    return !!this.call && !isEnded(this.call);
  }

  /**
   * 7.10.30: the mic of a call you're back on follows your own Mute (and the server's hold), not whatever it was when
   * it went on hold. Resuming a held caller after a swap left the mic muted (the app mutes a call it puts on hold).
   */
  applyMute(c: PhoneCall) {
    if (c.userMuted || c.serverHeld) c.muteAudio();
    else c.unmuteAudio();
  }

  /**
   * 7.10.30: puts `c` on hold. @telnyx/webrtc's hold() doesn't throw when the phone system refuses: it resolves false.
   * That was taken as held, and the "held" caller heard your other conversation. Now its mic is turned off at least.
   */
  private async holdOrMute(c: PhoneCall) {
    const ok = await Promise.resolve(c.hold()).then((r) => r !== false, () => false);
    if (!ok) c.muteAudio();
    return ok;
  }

  /** 7.10.30: hangs up the call on hold, keeping the one you're on. */
  async hangupHeld() {
    const h = this.held;
    if (!h) return;
    this.held = null;
    this.emit();
    if (!isEnded(h)) await Promise.resolve(h.hangup()).catch(() => {});
    this.emit();
  }

  /** 7.10.30: a call is being placed (between tapping Call and the call existing): a second tap doesn't place another. */
  private dialing = false;

  /** Any call in progress on this phone, including one on hold. */
  private busy() {
    return this.hasActiveCall() || (!!this.held && !isEnded(this.held));
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  /** Tries again later, backing off, unless signed out, already connected, or busy on a call. */
  private scheduleReconnect(socketClosed = false) {
    if (this.stopped || this.ready || this.reconnectTimer) return;
    if (this.busy()) {
      // 1.10.28: only if the line is still down when the call ends (the SDK usually reconnects by itself in a
      // second; the healthy line was rebuilt after every call that had a Wi-Fi blip).
      this.lostDuringCall = true;
      return;
    }
    // The connection just closed: the SDK reconnects by itself in ~1 s and keeps the same login. Give it 5 s
    // before building a new line (at 2 s the app replaced the SDK's own reconnect mid-way).
    const delay = Math.max(socketClosed && this.reconnectAttempt === 0 ? 5_000 : 0, RECONNECT_DELAYS[this.reconnectAttempt] ?? RECONNECT_EVERY);
    this.reconnectAttempt++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.stopped || this.ready) return;
      this.connect().catch(() => {}); // a failure schedules the next try
    }, delay);
  }

  /** Signs in again with a fresh token before the current one expires (when no call is in progress). */
  private scheduleRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      if (this.stopped) return;
      this.token = null; // the point of this reconnect is a fresh login
      if (this.busy()) {
        this.reconnectAfterCall = true;
        return;
      }
      this.reconnectNow();
    }, TOKEN_REFRESH_MS);
  }

  /** Drops the current connection and signs in again with a fresh token. */
  private reconnectNow() {
    if (this.stopped) return;
    this.clearReconnectTimer();
    this.ready = false;
    this.emit();
    this.connect().catch(() => {});
  }

  onChange(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private emit() {
    this.updateWaitingBeep();
    this.releaseSpeakerWhenIdle();
    for (const fn of this.listeners) fn();
  }

  /**
   * No call (ringing, live or held): the call speaker lets go of the last call's audio. Left attached, iPhone kept a
   * "now playing" card (joytravelinc.com, with a pause button) on the lock screen all day.
   */
  private releaseSpeakerWhenIdle() {
    const live = (c: PhoneCall | null | undefined) => !!c && !isEnded(c);
    if (live(this.call) || live(this.held)) return;
    if (this.silent && !live(this.waiting)) this.releaseSilent();
    const el = this.remoteEl();
    if (!el || (!el.srcObject && el.paused)) return;
    try {
      el.pause();
      el.srcObject = null;
      el.removeAttribute("src");
    } catch {
      // nothing attached
    }
    try {
      if (navigator.mediaSession) navigator.mediaSession.playbackState = "none";
    } catch {
      // no Media Session API
    }
  }

  /** Signs in with a fresh 24-hour token. Safe to call again; it reconnects only if needed. */
  connect(): Promise<void> {
    this.stopped = false;
    if (this.ready) return Promise.resolve();
    this.connecting ??= this.doConnect()
      .catch((err) => {
        this.scheduleReconnect();
        throw err;
      })
      .finally(() => (this.connecting = null));
    return this.connecting;
  }

  /** The line's login (valid ~24 h): reused for 12 h, so a reconnect doesn't wait on the server and Telnyx each time. */
  private token: { token: string; sipUsername: string; at: number } | null = null;
  private async lineToken(): Promise<{ token: string; sipUsername: string }> {
    if (this.token && Date.now() - this.token.at < 12 * 3600_000) return this.token;
    const t = await api.voiceToken();
    this.token = { ...t, at: Date.now() };
    return t;
  }

  private async doConnect() {
    // Never tear down the line in the middle of a call; reconnect once it ends.
    if (this.busy()) {
      this.reconnectAfterCall = true;
      return;
    }
    const { token, sipUsername } = await this.lineToken();
    if (this.busy()) {
      this.reconnectAfterCall = true;
      return;
    }
    this.sipUsername = sipUsername;
    const old = this.client;
    this.client = null; // so the old connection closing isn't treated as a dropped line
    // Not awaited: the SDK's disconnect waits up to 10 s to upload the last call's report first (measured 10,004 ms),
    // which held up every reconnect right after a call. The old client's events are ignored from here on.
    void old?.disconnect().catch(() => {});
    // Faster call setup: gather network paths early and send them as found (trickle). Never force TURN relay
    // (it adds delay). Region: nearest Telnyx media gateway if the teammate picked one, else automatic routing.
    const dp = getDevicePrefs();
    // The fastest region the speed test measured on this device (automatic), or the one picked by hand; during a
    // speed test, the region being measured.
    const region = this.regionOverride ?? effectiveRegion(dp);
    this.region = region;
    const client = new TelnyxRTC({
      login_token: token,
      prefetchIceCandidates: true,
      trickleIce: true,
      // A brief drop of the signaling connection (Wi-Fi hiccup) doesn't cut the call's audio.
      keepConnectionAliveOnSocketClose: true,
      forceRelayCandidate: false,
      ...(region ? { region } : {}),
    });
    client.remoteElement = "remote-audio";
    client.on("telnyx.ready", () => {
      if (this.client !== client) return;
      this.ready = true;
      this.lostDuringCall = false; // back by itself: nothing to rebuild after the call
      this.reconnectAttempt = 0;
      this.clearReconnectTimer();
      this.scheduleRefresh();
      this.emit();
    });
    const lost = (e?: unknown) => {
      if (this.client !== client) return; // an old connection we replaced
      // An error about one call (mic failed, a call's setup failed) isn't a dropped line: before 17.10.14 it
      // rebuilt the whole line, and calls arriving meanwhile were missed on this device.
      if ((e as { callId?: string } | undefined)?.callId) return;
      if (e) this.token = null; // a line error: get a fresh login next time
      this.ready = false;
      this.emit();
      this.scheduleReconnect(!e);
    };
    client.on("telnyx.socket.close", () => lost());
    client.on("telnyx.error", (e: unknown) => lost(e));
    client.on("telnyx.notification", (n: { type: string; call?: PhoneCall }) => {
      // Only the current line: a replaced line still closing could otherwise hand over a call that dies with it.
      if (this.client !== client) return;
      if (n.type === "callUpdate" && n.call) this.onCall(n.call);
    });
    this.client = client;
    // Incoming calls take the line's mic / speaker / voice processing settings when they arrive. Not awaited:
    // nothing in it needs the mic any more, and connecting doesn't wait for it.
    void this.applyAudioSettings();
    void this.applySpeakerNow();
    // Resolve only once Telnyx says the line is ready, so nothing races a half-open connection.
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("Couldn't reach the phone network")), 15_000);
      client.on("telnyx.ready", () => {
        clearTimeout(timer);
        resolve();
      });
      client.on("telnyx.error", (e: unknown) => {
        if ((e as { callId?: string } | undefined)?.callId) return; // about a call, not the line
        clearTimeout(timer);
        reject(new Error((e as { error?: { message?: string } })?.error?.message ?? "Phone line error"));
      });
      client.connect().catch((e: Error) => {
        clearTimeout(timer);
        reject(e);
      });
    });
  }

  // ------------------------------------------------------------ audio devices

  /** Puts the saved mic + echo cancellation / noise suppression / auto gain on the line (used by calls that arrive next). */
  private async applyAudioSettings() {
    const client = this.client;
    if (!client) return;
    const p = getDevicePrefs();
    try {
      // The chosen mic as a preference (deviceId "ideal"), not micId: with micId the SDK opened and closed the mic
      // to look the device up, here and again at the start of every call (an extra 50–500 ms per call).
      await client.setAudioSettings({
        ...(p.micId ? { deviceId: { ideal: p.micId } } : {}),
        echoCancellation: p.echoCancellation,
        noiseSuppression: p.noiseSuppression,
        autoGainControl: p.autoGainControl,
      });
    } catch {
      // unknown mic or unsupported constraint: the SDK keeps its defaults
    }
    try {
      client.speaker = p.speakerId || (null as unknown as string);
    } catch {
      // no setSinkId in this browser
    }
  }

  private remoteEl() {
    return document.getElementById("remote-audio") as HTMLAudioElement | null;
  }

  private async applySpeakerNow() {
    const el = this.remoteEl();
    if (el) await applySpeaker(el);
  }

  /** Choose the call speaker; switches right away (where the browser can) and for later calls. */
  async setSpeaker(deviceId: string): Promise<boolean> {
    setDevicePrefs({ speakerId: deviceId }); // listener applies it to the line + #remote-audio
    const el = this.remoteEl();
    const ok = el ? await applySpeaker(el, deviceId) : false;
    for (const c of [this.call, this.held]) {
      if (c && !isEnded(c) && c.setAudioOutDevice) await c.setAudioOutDevice(deviceId).catch(() => false);
    }
    return ok;
  }

  /** Choose the call microphone; switches mid-call (SDK replaces the track, keeping mute) and is used for later calls. */
  async setMic(deviceId: string): Promise<void> {
    setDevicePrefs({ micId: deviceId });
    await this.applyAudioSettings();
    const live = [this.call, this.held].filter((c): c is PhoneCall => !!c && !isEnded(c) && c.state !== "ringing" && c.state !== "new");
    for (const c of live) {
      // 1.10.28: a call going without the mic stays that way (switching it would quietly open the mic while the
      // screen still says it's off); "Turn on mic" uses the new one.
      if (!c.setAudioInDevice || (c as { micOff?: boolean }).micOff) continue;
      // "System default": the real id of the system's mic ("default" only exists in Chrome; elsewhere it failed silently).
      let id = deviceId;
      if (!id) {
        const mics = await navigator.mediaDevices.enumerateDevices().then((d) => d.filter((x) => x.kind === "audioinput")).catch(() => [] as MediaDeviceInfo[]);
        id = mics.find((m) => m.deviceId === "default")?.deviceId ?? mics[0]?.deviceId ?? "default";
      }
      try {
        await c.setAudioInDevice(id, c.isAudioMuted);
      } catch {
        // couldn't switch mid-call: the next call uses it
      }
    }
  }

  /** Plays this call's audio on #remote-audio (with two calls, the SDK leaves the last answered one there). */
  /**
   * Wi-Fi: mark this call's audio as voice (DSCP "expedited forwarding"). Wi-Fi routers with WMM (almost all of
   * them) then send it in their voice queue, ahead of downloads, video and uploads on the same network, so
   * a busy Wi-Fi adds less delay and drops fewer packets. Browsers that can't mark packets just ignore it.
   */
  private prioritized = new WeakSet<object>();
  private prioritizeAudio(c: PhoneCall) {
    // Off since 67.10.8: after 58.10.8 the computer's call delay went from ~76 ms to 217–340 ms while phones
    // (which don't apply the marking) stayed at ~65 ms. Some routers handle marked voice traffic badly.
    if (!PRIORITIZE_AUDIO) return;
    const pc = (c as unknown as { peer?: { instance?: RTCPeerConnection } }).peer?.instance;
    if (!pc || this.prioritized.has(pc)) return;
    // Only while it's still ringing: never change anything at the moment the call connects or mid-conversation.
    if (c.state === "active") return void this.prioritized.add(pc);
    if (!pc.getSenders().some((x) => x.track?.kind === "audio" && x.getParameters().encodings?.length)) return; // not ready yet: try on the next update
    this.prioritized.add(pc);
    for (const sender of pc.getSenders()) {
      if (sender.track?.kind !== "audio") continue;
      try {
        const params = sender.getParameters();
        if (!params.encodings?.length) continue;
        params.encodings = params.encodings.map((e) => ({ ...e, priority: "high", networkPriority: "high" }) as RTCRtpEncodingParameters);
        void sender.setParameters(params).catch(() => {});
      } catch {
        // not supported here
      }
    }
  }

  /**
   * 1.10.27, measured: on a call that comes in, the browser sends in the format the caller's side listed first
   * (Telnyx: G.722), whatever the app asked for, so "Phone-line (G.711)" never applied to incoming calls. With it
   * picked, the browser is told to send G.711, once, as soon as the call's audio is agreed. HD (the default) needs
   * nothing: G.722 and Opus are both HD.
   */
  private codecSet = new WeakSet<object>();
  private sendPickedCodec(c: PhoneCall) {
    if (getDevicePrefs().codec !== "pcmu") return;
    const pc = (c as unknown as { peer?: { instance?: RTCPeerConnection } }).peer?.instance;
    if (!pc || this.codecSet.has(pc) || !pc.remoteDescription || !pc.localDescription) return;
    this.codecSet.add(pc);
    for (const sender of pc.getSenders()) {
      if (sender.track?.kind !== "audio") continue;
      try {
        const params = sender.getParameters() as RTCRtpSendParameters & { codecs?: { mimeType: string; clockRate: number; channels?: number; sdpFmtpLine?: string }[] };
        const g711 = params.codecs?.find((x) => /PCMU/i.test(x.mimeType)) ?? params.codecs?.find((x) => /PCMA/i.test(x.mimeType));
        if (!g711 || !params.encodings?.length || /PCM[UA]/i.test(params.codecs?.[0]?.mimeType ?? "")) continue;
        params.encodings = params.encodings.map((e, i) => (i ? e : ({ ...e, codec: { mimeType: g711.mimeType, clockRate: g711.clockRate, channels: g711.channels, sdpFmtpLine: g711.sdpFmtpLine } } as RTCRtpEncodingParameters)));
        void sender.setParameters(params).catch(() => {}); // browsers without per-call codec choice: unchanged
      } catch {
        // not supported here
      }
    }
  }

  private attachRemote(c: PhoneCall) {
    const el = this.remoteEl();
    const stream = c.remoteStream;
    if (el && stream && el.srcObject !== stream) {
      // Already playing these same tracks (ringing audio that turns into the answered call): leave the speaker
      // alone. Re-attaching restarts playback and can swallow the first words.
      const ids = (s: MediaStream | null) => (s ? s.getAudioTracks().map((t) => t.id).sort().join(",") : "");
      const cur = el.srcObject instanceof MediaStream ? el.srcObject : null;
      if (cur && ids(cur) && ids(cur) === ids(stream) && !el.paused) return;
      el.srcObject = stream;
      void el.play().catch(() => {});
    }
  }

  // ------------------------------------------------------------ incoming calls

  /** Flags a new inbound call as a monitor leg (X-Joy-Monitor header, or expectMonitor() window). */
  private tagMonitor(c: PhoneCall) {
    // (A brand-new incoming call has no direction yet, but its headers are already there.)
    if (c.isMonitor !== undefined || c.direction === "outbound") return;
    // A teammate calling from their app ("id:Name"): shown by name, not as a customer.
    const ic = c.options.customHeaders?.find((h) => h.name.toLowerCase() === "x-joy-intercom");
    if (ic && !c.intercomName) c.intercomName = ic.value.split(":").slice(1).join(":").trim() || "Teammate";
    // The server marks a listen-in leg with "X-Joy-Role: monitor" (older builds: "X-Joy-Monitor: <mode>").
    const role = c.options.customHeaders?.find((h) => h.name.toLowerCase() === "x-joy-role" && h.value.trim().toLowerCase() === "monitor");
    const header = c.options.customHeaders?.find((h) => h.name.toLowerCase() === MONITOR_HEADER) ?? role;
    if (header) {
      const v = header.value.trim().toLowerCase() as MonitorMode;
      c.isMonitor = true;
      c.monitorMode = MONITOR_MODES.includes(v) ? v : (this.monitorExpect?.mode ?? this.monitoring?.mode ?? "listen");
      this.monitorExpect = null;
      return;
    }
    // A call marked as a customer's (X-Joy-Line) or a teammate's is never the listen-in leg, even inside the window.
    const marked = !!ic || !!c.options.customHeaders?.some((h) => h.name.toLowerCase() === "x-joy-line");
    if (c.state === "ringing" && !marked && this.monitorExpect && Date.now() < this.monitorExpect.until && !this.busy()) {
      c.isMonitor = true;
      c.monitorMode = this.monitorExpect.mode;
      this.monitorExpect = null;
      return;
    }
    if (c.state === "ringing") c.isMonitor = false;
  }

  /** Answers a monitor leg without ringing; "listen" starts with the mic muted. */
  private answerMonitor(c: PhoneCall) {
    c.autoAnswered = true;
    this.autoAnswer = null;
    const listen = (c.monitorMode ?? "listen") === "listen";
    if (listen) c.muteAudio(); // the SDK keeps this and applies it once the mic stream exists
    Promise.resolve(this.answerCall(c))
      .then(() => {
        if (listen) c.muteAudio();
      })
      .catch(() => {});
    if (!this.monitoring) this.monitoring = { callId: c.options.telnyxCallControlId ?? c.id, mode: c.monitorMode ?? "listen" };
  }

  /** Copies of a call that's already ringing here (see onCall): never shown, never rung. */
  private duplicateIds = new Set<string>();

  /**
   * Set by the app: an incoming call this device must not take at all (it's for the other line: your personal line
   * vs Joy Travel's). It's decided once, when the call first appears, and such a call never takes the call slot, never
   * rings, never shows and never counts as "on a call"; it simply keeps ringing on the devices on its own line.
   */
  ignoreIncoming: ((c: PhoneCall) => boolean) | null = null;
  private ignoredIds = new Set<string>();
  private ignoredCalls = new Map<string, PhoneCall>();
  /**
   * You switched lines: a call still ringing that was for the other line may be for this one now (and the other
   * way round is handled by the app). It shows and rings here from now on.
   */
  recheckIgnored() {
    for (const [id, c] of this.ignoredCalls) {
      if (isEnded(c)) {
        this.ignoredCalls.delete(id);
        this.ignoredIds.delete(id);
        continue;
      }
      if (this.ignoreIncoming?.(c)) continue;
      this.ignoredCalls.delete(id);
      this.ignoredIds.delete(id);
      this.onCall(c);
    }
  }

  /**
   * 5.10.30 "a call comes in and I suddenly stop hearing the caller (desktop and phone)": the calling library plays
   * every call into the one #remote-audio speaker, and the last one to get audio wins (its own warning 33011, "A
   * second inbound call rang into a session using one remoteElement"). A second call ringing in (call waiting, the
   * other line, a copy) took the speaker from the caller you were talking to, and when it stopped ringing the library
   * emptied the speaker, so the caller never came back. Now every call that isn't the one you're on plays into its
   * own silent element, and your call is put back on the speaker after any other call's update.
   */
  private sideEls = new Map<string, HTMLAudioElement>();
  private onCall(c: PhoneCall) {
    const cur = this.call;
    const busy = !!cur && !isEnded(cur) && cur.id !== c.id && this.held?.id !== c.id;
    if (busy && !isEnded(c) && !this.sideEls.has(c.id)) {
      try {
        const el = document.createElement("audio");
        el.muted = true;
        c.options.remoteElement = el;
        this.sideEls.set(c.id, el);
      } catch {
        // no DOM (tests)
      }
    }
    this.onCallNow(c);
    if (!c.isMonitor) this.micWhileRinging(c);
    const side = this.sideEls.get(c.id);
    if (side && isEnded(c)) {
      try {
        side.srcObject = null;
      } catch {
        // already released
      }
      this.sideEls.delete(c.id);
    }
    // The one you're on now: back on the speaker (it may have been taken, or emptied, by the other call).
    const now = this.call;
    if (now && now.id !== c.id && !isEnded(now) && (now.state === "active" || now.state === "early")) this.attachRemote(now);
    // A call that became the one you're on (you answered the waiting call): the library plays it on the speaker again.
    if (now && this.sideEls.has(now.id)) {
      now.options.remoteElement = "remote-audio";
      this.sideEls.delete(now.id);
    }
  }

  private onCallNow(c: PhoneCall) {
    if (this.duplicateIds.has(c.id)) {
      if (isEnded(c)) this.duplicateIds.delete(c.id);
      return;
    }
    if (this.ignoredIds.has(c.id)) {
      if (isEnded(c)) (this.ignoredIds.delete(c.id), this.ignoredCalls.delete(c.id));
      return;
    }
    // Speed test calls are measured by testCall(); they never show up as calls.
    // (By its number alone: a new call has no direction yet, and the number may come back without the "+". Missing
    // that, the test call itself counted as "a call came in" and every test stopped after a split second.)
    if (this.testCallIds.has(c.id) || isSpeedTestCall(c)) {
      if (isEnded(c)) this.testCallIds.delete(c.id);
      return;
    }
    this.tagMonitor(c);
    const ended = isEnded(c);
    this.watchTheirHangup(c);
    // A call for the other line: not ours here (decided on first sight, before it can take a slot).
    if (!ended && c.direction !== "outbound" && !c.isMonitor && this.call?.id !== c.id && this.held?.id !== c.id && this.waiting?.id !== c.id
      && ["new", "ringing", "early", "trying"].includes(c.state) && this.ignoreIncoming?.(c)) {
      this.ignoredIds.add(c.id);
      this.ignoredCalls.set(c.id, c);
      return;
    }
    if (!c.isMonitor) {
      if (this.trace?.id === c.id) {
        this.traceEvent(`state ${c.state}`);
        if (c.state === "active" && c.direction === "inbound" && !this.trace.stopAt) this.trace.stopAt = Date.now() + 10_000;
      }
      else if (!ended && (!this.call || this.call.id === c.id || isEnded(this.call))) this.traceStart(c);
      if (ended && this.trace?.id === c.id) this.traceStop();
      if (ended) this.reportEnd(c);
    }

    if (this.waiting?.id === c.id) {
      this.waiting = ended ? null : c;
    } else if (this.held?.id === c.id) {
      this.held = ended ? null : c;
    } else if (this.call?.id === c.id) {
      if (!ended) this.call = c;
      else {
        // They hung up on a call you were talking on (not you, not a listen-in): the app plays the hang-up tone.
        if (this.theirBye.has(c.id) && this.talked.has(c.id) && !c.isMonitor) this.theyHungUp = true;
        this.onPrimaryEnded();
      }
    } else if (!ended) {
      if (this.hasActiveCall()) {
        // Call waiting: a second call while busy is shown (with a soft beep), not answered over the current one.
        if (c.direction === "inbound" && c.state === "ringing") {
          // The same call arriving twice while it's still ringing here (the line was registered twice for a moment,
          // or the phone system sent it again): that's not a second caller. Ignore the copy, so you don't hear a
          // second ring (the call-waiting beep) on top of the first.
          const cur = this.call!;
          // 1.10.28: by caller number and name only when a session id is missing: two different callers with the same
          // caller ID (two "anonymous" callers, a hotel's main line) are two calls, and the second one was hidden.
          const bothIds = !!c.options.telnyxSessionId && !!cur.options.telnyxSessionId;
          const sameCall = cur.direction === "inbound" && cur.state === "ringing" &&
            (bothIds ? c.options.telnyxSessionId === cur.options.telnyxSessionId
              : !!c.options.remoteCallerNumber && c.options.remoteCallerNumber === cur.options.remoteCallerNumber &&
                (c.options.remoteCallerName ?? "") === (cur.options.remoteCallerName ?? ""));
          if (sameCall) {
            this.duplicateIds.add(c.id);
            return;
          }
          if (c.isMonitor || this.monitoring || this.call?.isMonitor || (this.waiting && !isEnded(this.waiting))) {
            void Promise.resolve(c.hangup()).catch(() => {}); // can't take it here; it keeps ringing for teammates
          } else {
            this.waiting = c;
            this.lateNote(this.call!, `another call is waiting (${c.options.remoteCallerNumber ?? "unknown"})`);
            // 1.10.28: it's the call you just tapped Answer on (in the inbox): answer it. A call still only ringing
            // here stops ringing here (it keeps ringing for teammates); one you're talking on goes on hold.
            const a = this.autoAnswer;
            if (a && Date.now() < a.until && c.options.telnyxSessionId === a.sessionId) {
              this.autoAnswer = null;
              void this.answerWaiting(cur.state === "ringing" ? "end" : "hold").catch(() => {});
            }
          }
        }
      } else {
        this.call = c;
      }
    } else if (this.call && isEnded(this.call)) {
      this.onPrimaryEnded();
    }
    if (this.waiting && isEnded(this.waiting)) this.waiting = null;
    if (this.held && isEnded(this.held)) this.held = null;
    if (!this.call && !this.held) this.monitoring = null;

    if (!this.busy() && (this.reconnectAfterCall || this.lostDuringCall)) {
      // The line dropped or the token is due for renewal: reconnect now that the call is over.
      this.reconnectAfterCall = false;
      this.lostDuringCall = false;
      setTimeout(() => {
        if (!this.busy()) this.reconnectNow();
        else this.reconnectAfterCall = true;
      }, 500);
    }

    const cur = this.call;
    if (cur && cur.id === c.id && c.direction === "inbound" && c.state === "ringing" && !c.autoAnswered) {
      if (c.isMonitor) {
        this.answerMonitor(c);
      } else if (
        this.autoAnswer &&
        Date.now() < this.autoAnswer.until &&
        (!c.options.telnyxSessionId || c.options.telnyxSessionId === this.autoAnswer.sessionId)
      ) {
        this.autoAnswer = null;
        c.autoAnswered = true;
        // 1.10.28: if answering fails, it rings normally again (it stayed silent, with no message).
        void this.answerCall(c).catch(() => {
          c.autoAnswered = false;
          this.emit();
        });
      }
    }
    if (cur && cur.id === c.id && !ended) this.prioritizeAudio(c);
    if (!ended) this.sendPickedCodec(c);
    if (cur && cur.id === c.id && !ended) this.guardJitter(c);
    if (cur && cur.id === c.id && c.state === "active" && c.direction === "outbound" && !c.isMonitor) this.watchFarAudio(c);
    // "early" = the other end's real ringing / busy tone, before they answer (outgoing calls).
    if (cur && cur.id === c.id && (c.state === "active" || c.state === "early")) this.attachRemote(c);
    // Outgoing calls connect the app first (while it rings); the app starts this check when the other side answers.
    if (cur && cur.id === c.id && c.state === "active" && c.direction !== "outbound" && this.startWatched !== c.id) {
      this.startWatched = c.id;
      void this.watchStart(c);
    }
    this.emit();
  }

  /** The primary call ended: the held call (or else the waiting one) takes its place. */
  /** This call's delay readings, for the "calls got slower than the best measured" check. */
  private callRtts: number[] = [];
  /** Calls that were connected (you talked), and calls the other side ended (the phone system's "bye"). */
  private talked = new Set<string>();
  private theirBye = new Set<string>();
  private byeWatched = new WeakSet<PhoneCall>();
  /** Set when the other side hung up on your call; read once with takeTheyHungUp(). */
  private theyHungUp = false;
  takeTheyHungUp() {
    const v = this.theyHungUp;
    this.theyHungUp = false;
    return v;
  }
  /**
   * The calling library ends a call the other side hung up with hangup({initiator: "remote:telnyx_rtc.bye"}, false);
   * your own End button calls hangup() with nothing. That's how the app tells who hung up (24.10.18).
   */
  private watchTheirHangup(c: PhoneCall) {
    if (c.state === "active") this.talked.add(c.id);
    if (isEnded(c)) setTimeout(() => (this.talked.delete(c.id), this.theirBye.delete(c.id)), 5000);
    if (this.byeWatched.has(c)) return;
    this.byeWatched.add(c);
    const orig = (c as unknown as { hangup: (...a: unknown[]) => unknown }).hangup;
    if (typeof orig !== "function") return;
    (c as unknown as { hangup: (...a: unknown[]) => unknown }).hangup = (...a: unknown[]) => {
      const p = a[0] as { initiator?: string } | undefined;
      if (typeof p?.initiator === "string" && p.initiator.startsWith("remote:")) this.theirBye.add(c.id);
      return orig.apply(c, a);
    };
  }

  private onPrimaryEnded() {
    if (this.callRtts.length >= 3) recordCallDelay(this.region, summarize(this.callRtts).ms!);
    this.callRtts = [];
    this.lastBuf = null;
    this.call = null;
    const h = this.held && !isEnded(this.held) ? this.held : null;
    if (h) {
      this.held = null;
      this.call = h;
      if (this.autoResumeHeld && h.state === "held") {
        void Promise.resolve(h.unhold())
          .then(() => {
            this.applyMute(h); // 7.10.30
            this.attachRemote(h);
            this.emit();
          })
          .catch(() => {});
      } else {
        this.attachRemote(h);
      }
      return;
    }
    const w = this.waiting && !isEnded(this.waiting) ? this.waiting : null;
    if (w) {
      // it simply rings as a normal incoming call now
      this.waiting = null;
      this.call = w;
    }
  }

  private updateWaitingBeep() {
    // Only once you're talking: while your phone is still ringing, a beep on top of the ring sounded like two ringers.
    const on = this.waitingBeep && !!this.waiting && !isEnded(this.waiting) && this.call?.state === "active";
    if (on && !this.waitingBeepTimer) {
      playCallWaitingBeep();
      this.waitingBeepTimer = setInterval(() => {
        if (this.waiting && !isEnded(this.waiting)) playCallWaitingBeep();
      }, WAITING_BEEP_EVERY);
    } else if (!on && this.waitingBeepTimer) {
      clearInterval(this.waitingBeepTimer);
      this.waitingBeepTimer = null;
    }
  }

  /**
   * 2.10.29: the server says this call was just answered on another phone (yours or a teammate's). If it's still only
   * ringing here, it stops now: before, it rang on until the phone system's own cancel arrived a second or two later.
   * A call being answered here (or already answered) is left alone. Returns whether it stopped one.
   */
  answeredElsewhere(sessionId: string): boolean {
    const rings = (c: PhoneCall | null) =>
      !!c && c.direction !== "outbound" && !c.isMonitor && !c.autoAnswered && !this.answeringCalls.has(c.id)
      && ["new", "trying", "early", "ringing"].includes(c.state) && c.options.telnyxSessionId === sessionId;
    // You pressed Answer on this phone for it: it's this phone that won, so nothing to stop.
    if (this.autoAnswer?.sessionId === sessionId) return false;
    // Ignored for good (not in ignoredCalls, which come back when you switch lines): the phone system's cancel ends it.
    const drop = (c: PhoneCall) => this.ignoredIds.add(c.id);
    if (rings(this.waiting)) {
      drop(this.waiting!);
      this.waiting = null;
      this.emit();
      return true;
    }
    if (rings(this.call)) {
      const c = this.call!;
      drop(c);
      if (this.trace?.id === c.id) this.traceStop();
      this.onPrimaryEnded();
      this.emit();
      return true;
    }
    return false;
  }

  /** "Answer" for a call the server says is ringing. Answers here if it's ringing on this phone, otherwise asks to be rung. */
  async answerSessionCall(sessionId: string) {
    const c = this.call;
    const sid = c?.options.telnyxSessionId;
    // Already here but still setting up: it's answered the moment it rings (asking the server to ring this app again
    // would only bring a copy, which is dropped).
    if (c && c.direction !== "outbound" && ["new", "trying", "early"].includes(c.state) && (!sid || sid === sessionId)) {
      this.autoAnswer = { sessionId, until: Date.now() + 20_000 };
      return;
    }
    if (c && c.direction === "inbound" && c.state === "ringing" && (!sid || sid === sessionId)) {
      const { micBlocked } = await this.answerCall(c);
      if (micBlocked) throw new Error("Answered, but your microphone is blocked. Allow it in the browser, then tap Mic.");
      return;
    }
    const w = this.waiting;
    if (w && (!w.options.telnyxSessionId || w.options.telnyxSessionId === sessionId)) {
      await this.answerWaiting("hold");
      return;
    }
    // 2.10.29 "I press Answer on my phone and get another window to answer": an iPhone waking up reconnects its line
    // here, and the call that was ringing for it comes in during that reconnect, before this said "answer it". It
    // rang with its own Answer button, and the server's extra ring for this phone was dropped as a copy of it. Now
    // "answer it" is set before reconnecting, and a call that came in meanwhile is answered right here.
    this.autoAnswer = { sessionId, until: Date.now() + 20_000 };
    await this.connect();
    const here = this.call;
    if (here && here.direction === "inbound" && here.state === "ringing" && !here.autoAnswered && here.options.telnyxSessionId === sessionId) {
      this.autoAnswer = null;
      here.autoAnswered = true;
      this.emit();
      try {
        const { micBlocked } = await this.answerCall(here);
        if (micBlocked) throw new Error("Answered, but your microphone is blocked. Allow it in the browser, then tap Mic.");
      } catch (e) {
        here.autoAnswered = false; // rings normally again, with its message
        this.emit();
        throw e;
      }
      return;
    }
    if (here?.autoAnswered && here.options.telnyxSessionId === sessionId) return; // came in and answered by itself
    const wait = this.waiting;
    if (wait && wait.options.telnyxSessionId === sessionId && !isEnded(wait)) {
      this.autoAnswer = null;
      await this.answerWaiting("hold");
      return;
    }
    // 1.10.20 quicker: the mic opens while the server rings this app (in parallel), not after the call arrives
    // (that was the time the call sat "ringing" after you'd pressed Answer). Unused after 25 s: closed again.
    if (!this.startsWithoutMic()) {
      const stream = navigator.mediaDevices.getUserMedia({ audio: audioConstraints() }).catch(() => null);
      const mine = { stream, until: Date.now() + 25_000 };
      // 1.10.28: a second Answer tap before the call arrived left the first mic open for good (the mic light on).
      void this.takePreMic()?.then((s) => s?.getTracks().forEach((t) => t.stop()));
      this.preMic = mine;
      setTimeout(() => {
        if (this.preMic === mine) {
          this.preMic = null;
          void stream.then((s) => s?.getTracks().forEach((t) => t.stop()));
        }
      }, 25_000);
    }
    try {
      await api.pickup(sessionId);
    } catch (e) {
      this.autoAnswer = null; // don't auto-answer some later call
      const m = this.takePreMic();
      void m?.then((s) => s?.getTracks().forEach((t) => t.stop()));
      throw e;
    }
  }

  /**
   * The next inbound call to arrive within `ms` is a monitor leg: answered automatically and silently,
   * flagged `isMonitor`, mic muted for "listen". (Calls carrying an X-Joy-Monitor header are detected regardless.)
   */
  expectMonitor(ms = 20_000, mode: MonitorMode = "listen") {
    this.monitorExpect = { until: Date.now() + ms, mode };
  }

  /** Listen in on a teammate's call (admins). The server rings this phone; it's answered automatically. */
  async listenIn(callId: string, mode: MonitorMode) {
    if (this.busy()) throw new Error("Hang up your own call first");
    await this.connect();
    this.expectMonitor(20_000, mode);
    const mon = { callId, mode };
    this.monitoring = mon;
    // The monitor leg never came (failed on the server, or the teammate hung up first): don't stay "monitoring".
    clearTimeout(this.monitorTimer);
    this.monitorTimer = setTimeout(() => {
      if (this.monitoring === mon && !this.call?.isMonitor && !this.held?.isMonitor) {
        this.monitoring = null;
        this.monitorExpect = null;
        this.emit();
      }
    }, 21_000);
    try {
      await api.monitorCall(callId, mode);
    } catch (e) {
      this.monitorExpect = null;
      this.monitoring = null;
      throw e;
    }
    this.emit();
  }

  /**
   * Call waiting: answer the waiting call. "hold" puts the current call on hold (it becomes `held`;
   * swap() switches between them); "end" hangs up the current call first.
   */
  async answerWaiting(mode: "hold" | "end" = "hold") {
    const next = this.waiting;
    if (!next || isEnded(next)) {
      this.waiting = null;
      this.emit();
      return;
    }
    const cur = this.call && !isEnded(this.call) ? this.call : null;
    // 7.10.30: "Hold & answer" while your call is still connecting hung it up (only an active call can go on hold).
    // Your call is kept and the waiting call stays waiting: answer it once you're connected, or End & answer.
    if (cur && mode === "hold" && cur.state !== "active" && cur.state !== "held") {
      throw new Error("Your call is still connecting. Answer once it's connected, or use End & answer.");
    }
    this.waiting = null;
    // 10.10.30: the new call gets your mic while the first one goes on hold (it was opened afresh only after the
    // hold): a copy of the mic you're already talking on (instant), or a new one opening alongside the hold.
    const micForNext = next.options.localStream || this.startsWithoutMic() ? null : this.shareMic(cur);
    if (cur && mode === "hold") {
      // A call already on hold has to go: only two calls at once.
      if (this.held && !isEnded(this.held)) await Promise.resolve(this.held.hangup()).catch(() => {});
      if (cur.state !== "held") await this.holdOrMute(cur); // 7.10.30: a refused hold mutes it
      // The waiting caller hung up while we were putting this call on hold: take this one back off hold.
      if (isEnded(next)) {
        await Promise.resolve(cur.unhold()).catch(() => {});
        this.call = cur;
        if (this.held === cur) this.held = null;
        this.applyMute(cur);
        this.attachRemote(cur);
        this.emit();
        return;
      }
      this.held = cur;
    } else if (cur) {
      // 1.10.28: the ended call's delay readings are its own (they were counted as part of the next call).
      if (this.callRtts.length >= 3) recordCallDelay(this.region, summarize(this.callRtts).ms!);
      this.callRtts = [];
      this.lastBuf = null;
      // Switch first: hanging up the primary call must not promote (and un-hold) the held call.
      this.call = next;
      await Promise.resolve(cur.hangup()).catch(() => {});
    }
    this.call = next;
    this.emit();
    const shared = await micForNext;
    if (shared && !next.options.localStream && !isEnded(next)) next.options.localStream = shared;
    else shared?.getTracks().forEach((t) => t.stop());
    await this.answerCall(next);
    this.attachRemote(next);
    this.emit();
  }

  /** 10.10.30: a copy of the live mic `cur` is sending (unmuted for the new call), else a new mic opening now. */
  private async shareMic(cur: PhoneCall | null): Promise<MediaStream | null> {
    const live = cur?.options.localStream?.getAudioTracks().find((t) => t.readyState === "live");
    if (live && cur?.options.localStream !== this.silent?.stream) {
      try {
        const copy = live.clone();
        copy.enabled = true;
        return new MediaStream([copy]);
      } catch {
        // can't copy: open one below
      }
    }
    return navigator.mediaDevices?.getUserMedia ? navigator.mediaDevices.getUserMedia({ audio: audioConstraints() }).catch(() => null) : null;
  }

  /** Call waiting: end the current call and answer the waiting one. (Same as answerWaiting("end").) */
  async swapToWaiting() {
    await this.answerWaiting("end");
  }

  /** Call waiting: decline the waiting call on this phone (it keeps ringing for teammates). */
  async declineWaiting() {
    const w = this.waiting;
    this.waiting = null;
    this.emit();
    if (w) await Promise.resolve(w.hangup()).catch(() => {});
    this.emit();
  }

  /** Switches between the active call and the held one (or resumes the held call if it's the only one). */
  async swap() {
    const h = this.held;
    if (!h || isEnded(h)) {
      this.held = null;
      const c = this.call;
      if (c && c.state === "held") {
        await Promise.resolve(c.unhold()).catch(() => {});
        this.applyMute(c); // 7.10.30
        this.attachRemote(c);
      }
      this.emit();
      return;
    }
    const cur = this.call && !isEnded(this.call) ? this.call : null;
    if (cur && cur.state !== "held") await this.holdOrMute(cur); // 7.10.30: a refused hold mutes it
    // The held caller hung up meanwhile: stay with the current call (back off hold), never switch to an ended one.
    if (isEnded(h)) {
      if (cur) {
        await Promise.resolve(cur.unhold()).catch(() => {});
        this.applyMute(cur);
        this.attachRemote(cur);
      }
      this.held = null;
      this.emit();
      return;
    }
    this.held = cur;
    this.call = h;
    this.emit();
    await Promise.resolve(h.unhold()).catch(() => {});
    this.applyMute(h); // 7.10.30: your mic is live again (unless you muted this call yourself)
    this.attachRemote(h);
    this.emit();
  }

  async dial(e164: string, myName: string) {
    // 7.10.30: a second Call tap while the first was still connecting the line placed a second call; the first was
    // lost with its mic still open. The tap that comes first places the call; the other is refused.
    if (this.busy() || this.dialing) throw new Error("You're already on a call");
    this.dialing = true;
    try {
      await this.dialNow(e164, myName);
    } finally {
      this.dialing = false;
    }
  }

  private async dialNow(e164: string, myName: string) {
    this.lastBuf = null;
    await this.connect();
    if (!this.ready) throw new Error("Phone line isn't connected");
    if (!this.client) throw new Error("Phone line isn't connected");
    const p = getDevicePrefs();
    // Our calling ID as caller number lets the server tell which teammate placed the call.
    // (No custom SIP headers: calls with an extra X- header measured slower in 35/36.10.8.)
    const noMic = this.startsWithoutMic();
    const call = this.client.newCall({
      destinationNumber: e164,
      callerNumber: this.sipUsername,
      callerName: myName,
      audio: audioConstraints(), // the chosen mic as a preference: one mic open per call (micId made it two)
      video: false,
      // iPhone with the Mic switch off: no mic (and no "Allow microphone?") until you tap Mic in the call.
      ...(noMic ? { localStream: this.silentStream() } : {}),
      ...(p.speakerId ? { speakerId: p.speakerId } : {}),
      ...(preferredCodecs() ? { preferred_codecs: preferredCodecs() } : {}),
    }) as unknown as PhoneCall;
    if (noMic) call.micOff = true;
    this.call = call;
    this.emit();
  }

  /** Calls a teammate's app (no customer, no recording). */
  async dialTeammate(userId: number, name: string, myName: string) {
    await this.dial(intercomNumber(userId), myName);
    if (this.call) this.call.intercomName = name;
    this.emit();
  }

  // ------------------------------------------------------------ the Mic switch (iPhone)
  //
  // iPhone asks "Allow microphone?" whenever a page opens the mic while it isn't already open. With the Mic switch on,
  // the mic stays open, so calls never ask. With it off, a call doesn't open the mic by itself any more (that's what
  // brought up the prompt mid-call): it starts with silence, and the mic opens only when you tap Mic in the call.

  private silent: { ctx: AudioContext; stream: MediaStream } | null = null;

  /** Should this call start without the mic? */
  private startsWithoutMic(): boolean {
    return isIOS() && !micIsOn() && !micOpensSilently();
  }

  private silentStream(): MediaStream {
    if (!this.silent) {
      const ctx = new AudioContext();
      this.silent = { ctx, stream: ctx.createMediaStreamDestination().stream };
      // The silence is just a placeholder track: the context doesn't need to run. A running AudioContext next to a
      // call makes iPhone call audio crackle and stutter (WebKit 224874 / 225880), so it's paused at once.
      void ctx.suspend().catch(() => {});
    }
    return this.silent.stream;
  }

  private releaseSilent() {
    const s = this.silent;
    this.silent = null;
    s?.stream.getTracks().forEach((t) => t.stop());
    void s?.ctx.close().catch(() => {});
  }

  /** Answers `c`: with the mic, or (iPhone, Mic switch off) without it, and no permission prompt. */
  /**
   * 1.10.20: one answer per call. While the app answered a call by itself (Answer pressed in the inbox, the server
   * rang this app), the incoming window could still show its own Answer for that call; pressing it answered the same
   * call a second time (measured: 2 answers on one call). A second press now waits for the first answer instead.
   */
  private answeringCalls = new Map<string, Promise<{ micBlocked: boolean }>>();
  answerCall(c: PhoneCall): Promise<{ micBlocked: boolean }> {
    const busy = this.answeringCalls.get(c.id);
    if (busy) return busy;
    const p = this.answerCallOnce(c).finally(() => setTimeout(() => this.answeringCalls.delete(c.id), 5000));
    this.answeringCalls.set(c.id, p);
    return p;
  }
  /** The mic opened when you pressed Answer in the inbox, ready for the call the server is ringing here. */
  private preMic: { stream: Promise<MediaStream | null>; until: number; callId?: string } | null = null;
  /**
   * 9.10.30 "latency on my side when calls just start": answering in the incoming call window opened the mic only at
   * the tap (a few hundred ms; 1-2 s with a Bluetooth headset switching over), so your first words were lost. The mic
   * now starts opening while the call rings (as Answer in the inbox already did, 1.10.20), and closes again if it
   * stops ringing unanswered. Not on iPhone with the Mic switch off (that one never opens the mic by itself).
   */
  private micWhileRinging(c: PhoneCall) {
    const pre = this.preMic;
    if (isEnded(c)) {
      if (pre?.callId === c.id) {
        this.preMic = null;
        void pre.stream.then((st) => st?.getTracks().forEach((t) => t.stop()));
      }
      return;
    }
    if (pre || this.call?.id !== c.id || c.direction === "outbound" || c.state !== "ringing" || c.isMonitor || c.autoAnswered
      || c.options.localStream || this.startsWithoutMic() || !navigator.mediaDevices?.getUserMedia) return;
    const stream = navigator.mediaDevices.getUserMedia({ audio: audioConstraints() }).catch(() => null);
    const mine = { stream, until: Date.now() + 60_000, callId: c.id };
    this.preMic = mine;
    setTimeout(() => {
      if (this.preMic === mine) {
        this.preMic = null;
        void stream.then((st) => st?.getTracks().forEach((t) => t.stop()));
      }
    }, 60_000);
  }
  /** The line dropped during a call and hadn't come back yet (1.10.28). */
  private lostDuringCall = false;
  private takePreMic(): Promise<MediaStream | null> | null {
    const m = this.preMic;
    this.preMic = null;
    return m && Date.now() < m.until ? m.stream : null;
  }
  private async answerCallOnce(c: PhoneCall): Promise<{ micBlocked: boolean }> {
    let micBlocked = false;
    const pre = !c.options.localStream ? this.takePreMic() : null;
    if (pre) {
      const s = await pre;
      if (s && s.getAudioTracks().some((t) => t.readyState === "live")) c.options.localStream = s;
    }
    if (this.startsWithoutMic() && !c.options.localStream) {
      c.options.localStream = this.silentStream();
      c.micOff = true;
    } else if (!c.options.localStream) {
      // Open the mic first: when it fails (blocked, in use by another app, no mic) the phone library would
      // quietly hang the call up. Answer without it instead, so the call connects and you can turn it on.
      try {
        c.options.localStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints() });
      } catch {
        c.options.localStream = this.silentStream();
        c.micOff = true;
        micBlocked = true;
      }
    }
    // 7.10.30: the call ended while the mic was opening: close the mic (it stayed on, the mic light lit) and don't answer.
    if (isEnded(c)) {
      const s = c.options.localStream;
      if (s && s !== this.silent?.stream) s.getTracks().forEach((t) => t.stop());
      return { micBlocked: false };
    }
    await c.answer();
    this.emit();
    return { micBlocked };
  }

  /** You tapped Mic in a call that started without it: opens the mic now (iPhone may ask once) and sends it. */
  async enableMic(c: PhoneCall): Promise<boolean> {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints() });
    } catch {
      return false;
    }
    const track = stream.getAudioTracks()[0];
    const pc = (c as unknown as { peer?: { instance?: RTCPeerConnection } }).peer?.instance;
    const sender = pc?.getSenders().find((x) => x.track?.kind === "audio" || x.track === null);
    // 7.10.30: the call ended while the mic opened, or the new mic couldn't be sent: close it again (it stayed open).
    if (!track || !sender || isEnded(c)) {
      stream.getTracks().forEach((t) => t.stop());
      return false;
    }
    try {
      await sender.replaceTrack(track);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      return false;
    }
    const old = c.options.localStream;
    c.options.localStream = stream; // so Mute / Unmute act on the real mic from now on
    c.micOff = false;
    if (old && old !== stream && this.silent?.stream === old && !this.callsUsingSilent()) this.releaseSilent();
    this.emit();
    return true;
  }

  private callsUsingSilent(): boolean {
    const s = this.silent?.stream;
    return !!s && [this.call, this.held, this.waiting].some((x) => x && !isEnded(x) && x.options.localStream === s);
  }

  /** Silences the other side on this phone (used while the caller is on hold). */
  setRemoteMuted(muted: boolean) {
    const el = this.remoteEl();
    if (el) el.muted = muted;
  }

  // ------------------------------------------------------------ jitter protection
  /**
   * Jitter protection: the phone holds a little incoming audio before playing it (its jitter buffer), so packets that
   * arrive late or bunched up still play smoothly instead of crackling or cutting out.
   * By default the app leaves that buffer to the phone (Google's own adaptive jitter buffer, NetEQ, which sizes it from
   * the jitter it measures: no fixed minimum, ~25 ms on a calm line). Every extra millisecond of buffer is extra delay
   * on every word, so nothing is added while the call sounds fine.
   * Only when the phone actually had to make up audio because packets came in too late (concealment beyond what lost
   * packets explain, > 2% in 2 of the last 3 two-second windows) does the app ask for 40 ms more than the phone is holding (up to 200 ms). After
   * 4 calm seconds it steps back down 40 ms every 2 s, and once it's back near the phone's own level the hint is
   * removed, so the phone is in charge again.
   * Uses RTCRtpReceiver.jitterBufferTarget, which libwebrtc treats as a minimum (the phone can still go higher on its
   * own). Changes nothing on the network or with Telnyx, and never renegotiates the call.
   */
  private jb = new Map<string, { target: number | null; conceal: number; total: number; lost: number; recv: number; bufDelay: number; bufCount: number; calmSince: number; maxSet: number; at: number; pc: RTCPeerConnection | null; lateWins: boolean[] }>();
  private jbTimer: ReturnType<typeof setInterval> | null = null;
  private static readonly JB_STEP_MS = 40;
  private static readonly JB_MAX_MS = 200;
  private static jbSupported(): boolean {
    return typeof RTCRtpReceiver !== "undefined" && "jitterBufferTarget" in RTCRtpReceiver.prototype;
  }
  /** null = no hint: the phone decides on its own (the default). */
  private setJbTarget(pc: RTCPeerConnection, ms: number | null) {
    for (const r of pc.getReceivers()) {
      if (r.track?.kind !== "audio") continue;
      try {
        (r as RTCRtpReceiver & { jitterBufferTarget: number | null }).jitterBufferTarget = ms;
      } catch {
        // not supported here
      }
    }
  }
  private guardJitter(c: PhoneCall) {
    if (!Phone.jbSupported() || isSpeedTestCall(c as never)) return; // the speed test measures the raw line
    if (!this.jb.has(c.id)) this.jb.set(c.id, { target: null, conceal: 0, total: 0, lost: 0, recv: 0, bufDelay: 0, bufCount: 0, calmSince: Date.now(), maxSet: 0, at: 0, pc: null, lateWins: [] });
    if (!this.jbTimer) this.jbTimer = setInterval(() => void this.adjustJitter(), 2000);
  }
  private async adjustJitter() {
    const c = this.call;
    const live = new Set([this.call, this.held, this.waiting].filter((x): x is PhoneCall => !!x && !isEnded(x)).map((x) => x.id));
    for (const [id, st] of this.jb) if (!live.has(id)) {
      // Ended: its largest extra buffer goes into the call's audio report (kept even if the call screen wasn't open).
      const rep = this.audioReps.get(id);
      if (rep) rep.jbTargetMaxMs = st.maxSet;
      this.jb.delete(id);
    }
    if (!live.size && this.jbTimer) (clearInterval(this.jbTimer), (this.jbTimer = null));
    if (!c || isEnded(c)) return;
    const st = this.jb.get(c.id);
    const pc = (c as unknown as { peer?: { instance?: RTCPeerConnection } }).peer?.instance;
    if (!st || !pc) return;
    // The call's audio report is otherwise only updated while the call screen is open: keep it current when it isn't.
    if (Date.now() - (this.audioReps.get(c.id)?.endedAt ?? 0) > 3000) void this.quality();
    let conceal = 0, total = 0, lost = 0, recv = 0, bufDelay = 0, bufCount = 0;
    try {
      (await pc.getStats()).forEach((r: any) => {
        if (r.type !== "inbound-rtp" || r.kind !== "audio") return;
        conceal += (r.concealedSamples ?? 0) - (r.silentConcealedSamples ?? 0);
        total += r.totalSamplesReceived ?? 0;
        lost += r.packetsLost ?? 0;
        recv += r.packetsReceived ?? 0;
        bufDelay += r.jitterBufferDelay ?? 0;
        bufCount += r.jitterBufferEmittedCount ?? 0;
      });
    } catch {
      return;
    }
    if (this.call !== c) return; // swapped or ended while reading: next time
    const now = Date.now();
    // A fresh start for the numbers (first reading, back from hold / a swap, a reconnected call, counters reset):
    // only take a baseline this time, so the hold or the reconnect itself never counts as late audio.
    const fresh = st.pc !== pc || now - st.at > 3500 || total < st.total || recv < st.recv;
    if (st.pc !== pc && st.target != null) this.setJbTarget(pc, st.target); // a new connection gets the hint again
    const dTotal = total - st.total, dConceal = conceal - st.conceal, dLost = Math.max(0, lost - st.lost), dRecv = recv - st.recv;
    // What the phone is holding right now (last 2 s), ms.
    const holding = !fresh && bufCount > st.bufCount ? ((bufDelay - st.bufDelay) / (bufCount - st.bufCount)) * 1000 : null;
    Object.assign(st, { total, conceal, lost: Math.max(st.lost, lost), recv, bufDelay, bufCount, at: now, pc });
    if (fresh || dRecv < 50) return; // too little audio in this window to judge (~1 s at least)
    // Made up beyond what lost packets explain = packets that arrived too late to play. Waiting longer can't bring
    // back packets that never arrived, so loss alone never adds delay.
    const madeUp = dTotal > 0 ? dConceal / dTotal : 0;
    const lostShare = dLost + dRecv > 0 ? dLost / (dLost + dRecv) : 0;
    // Lost packets are only counted once a later one arrives, so one window can look "late" when it was loss:
    // react when 2 of the last 3 windows (2 s each) show late audio.
    // More than 2% of the audio arriving too late (the threshold in the WebRTC jitter-buffer tuning guide; 1% tripped
    // on ordinary Wi-Fi spikes, 9.10.18).
    st.lateWins = [...st.lateWins, madeUp - lostShare > 0.02].slice(-3);
    const late = st.lateWins.filter(Boolean).length >= 2;
    let next = st.target;
    if (late) {
      // First time: just keep what the phone already grew to (so it doesn't shrink straight back into the gaps).
      // After that: 40 ms more than our own hint, never stacked on the phone's extra (9.10.18: max(target, holding)
      // + 40 stacked our step on top of the browser's own growth, and the delay crept up to 200 ms).
      next = Math.min(Phone.JB_MAX_MS, st.target != null ? st.target + Phone.JB_STEP_MS : Math.max(40, Math.ceil((holding ?? 20) / 20) * 20));
      st.calmSince = now;
      st.lateWins = []; // the next raise needs fresh evidence
    } else if (st.target != null && now - st.calmSince >= 4000) {
      // Back down 40 ms per 2 s (the phone plays ~2% faster meanwhile: not audible); near its own level, hand it back.
      const down = st.target - Phone.JB_STEP_MS;
      next = down <= 40 ? null : down;
    }
    if (next !== st.target) {
      st.target = next;
      if (next != null) st.maxSet = Math.max(st.maxSet, next);
      this.setJbTarget(pc, next);
    }
  }

  /** Each call's running audio report, by call (calls on hold / waiting keep theirs). */
  private audioReps = new Map<string, AudioReport>();
  /** Reports of calls that have ended (each once), for the server's call audio log. */
  takeEndedAudioReports(): AudioReport[] {
    const live = new Set([this.call, this.held, this.waiting].filter((x): x is PhoneCall => !!x && !isEnded(x)).map((x) => x.id));
    const out: AudioReport[] = [];
    for (const [id, r] of this.audioReps) if (!live.has(id)) {
      const j = this.jb.get(id);
      if (j && r.jbTargetMaxMs != null) r.jbTargetMaxMs = Math.max(r.jbTargetMaxMs, j.maxSet);
      out.push(r);
      this.audioReps.delete(id);
    }
    return out;
  }

  /** Connection quality of the active call, or null when there's no call / no stats. */
  async quality(): Promise<CallQuality | null> {
    const call = this.call;
    const pc = (call as unknown as { peer?: { instance?: RTCPeerConnection } } | null)?.peer?.instance;
    if (!pc || !call || isEnded(call)) return null;
    let rtt: number | null = null, jitter: number | null = null, lost = 0, received = 0, relay: CallQuality["relay"] = null;
    let bufDelay: number | null = null, bufCount: number | null = null;
    let path: { ip: string; protocol: string; localType: string; remoteType: string } | null = null;
    // For the end-of-call audio report (what really happened to the sound): network vs this phone.
    const a = { concealed: 0, silentConcealed: 0, total: 0, events: 0, inserted: 0, removed: 0, synth: 0, playout: 0, codec: "", sendCodec: "", localType: "", network: "", farLost: null as number | null };
    try {
      const stats = await pc.getStats();
      const byId = new Map<string, any>();
      stats.forEach((r: any) => byId.set(r.id, r));
      stats.forEach((r: any) => {
        if (r.type === "candidate-pair" && (r.nominated || r.selected) && r.state === "succeeded") {
          if (typeof r.currentRoundTripTime === "number") rtt = Math.round(r.currentRoundTripTime * 1000);
          const local = byId.get(r.localCandidateId);
          if (local?.candidateType === "relay") relay = (local.relayProtocol ?? "udp") as CallQuality["relay"];
          // Where this call's audio actually goes (Telnyx's media server), for Settings › Call speed check.
          const remote = byId.get(r.remoteCandidateId);
          const ip = remote?.address ?? remote?.ip ?? null;
          if (ip) path = { ip, protocol: String(remote?.protocol ?? local?.protocol ?? ""), localType: String(local?.candidateType ?? ""), remoteType: String(remote?.candidateType ?? "") };
        }
        // 1.10.27: the format you send too (the one above is the one you hear).
        if (r.type === "outbound-rtp" && r.kind === "audio") {
          const sc = r.codecId ? byId.get(r.codecId) : null;
          if (sc?.mimeType) a.sendCodec = String(sc.mimeType).replace(/^audio\//, "");
        }
        if (r.type === "inbound-rtp" && r.kind === "audio") {
          a.concealed += r.concealedSamples ?? 0;
          a.silentConcealed += r.silentConcealedSamples ?? 0;
          a.total += r.totalSamplesReceived ?? 0;
          a.events += r.concealmentEvents ?? 0;
          a.inserted += r.insertedSamplesForDeceleration ?? 0;
          a.removed += r.removedSamplesForAcceleration ?? 0;
          const codec = r.codecId ? byId.get(r.codecId) : null;
          if (codec?.mimeType) a.codec = String(codec.mimeType).replace(/^audio\//, "");
          if (typeof r.jitter === "number") jitter = Math.round(r.jitter * 1000);
          if (typeof r.jitterBufferDelay === "number" && typeof r.jitterBufferEmittedCount === "number") {
            bufDelay = r.jitterBufferDelay;
            bufCount = r.jitterBufferEmittedCount;
          }
          lost += r.packetsLost ?? 0;
          received += r.packetsReceived ?? 0;
        }
        if (r.type === "remote-inbound-rtp" && r.kind === "audio" && rtt === null && typeof r.roundTripTime === "number") {
          rtt = Math.round(r.roundTripTime * 1000);
        }
        if (r.type === "remote-inbound-rtp" && r.kind === "audio" && typeof r.fractionLost === "number") a.farLost = Math.max(a.farLost ?? 0, r.fractionLost);
        // The phone's own speaker output: samples it had to make up because the audio arrived too late to play.
        if (r.type === "media-playout") {
          a.synth += r.synthesizedSamplesDuration ?? 0;
          a.playout += r.totalSamplesDuration ?? 0;
        }
        if (r.type === "candidate-pair" && (r.nominated || r.selected) && r.state === "succeeded") {
          const local = byId.get(r.localCandidateId);
          a.localType = String(local?.candidateType ?? "");
          a.network = String(local?.networkType ?? "");
        }
      });
    } catch {
      return null;
    }
    // The call ended or another one took its place while the numbers were read: they belong to no current call.
    if (this.call !== call || isEnded(call)) return null;
    const loss = received + lost > 0 ? lost / (received + lost) : null;
    const r = rtt as number | null, j = jitter as number | null;
    // Current jitter buffer: change in total delay / change in samples played since the last reading.
    let bufferMs: number | null = null;
    const bd = bufDelay as number | null, bc = bufCount as number | null;
    if (bd != null && bc != null) {
      const prev = this.lastBuf?.callId === this.call.id ? this.lastBuf : null; // (another call's reading never mixes in)
      if (prev && bc > prev.count) bufferMs = Math.round(((bd - prev.delay) / (bc - prev.count)) * 1000);
      else if (bc > 0) bufferMs = Math.round((bd / bc) * 1000);
      this.lastBuf = { delay: bd, count: bc, callId: this.call.id };
    }
    // Per-region call delay for Settings (at most every 10 s, so it adds next to nothing during a call).
    if (r != null && this.callRtts.length < 600) this.callRtts.push(r);
    if (r != null && Date.now() - this.lastRttSave > 10_000) {
      this.lastRttSave = Date.now();
      recordCallRtt(this.region, r);
    }
    const grade: CallQuality["grade"] = r === null && j === null ? "unknown"
      : (r ?? 0) > 400 || (j ?? 0) > 60 || (loss ?? 0) > 0.05 ? "poor"
      : (r ?? 0) > 200 || (j ?? 0) > 30 || (loss ?? 0) > 0.02 || relay === "tcp" || relay === "tls" ? "fair" : "good";
    // Keep the running totals for the end-of-call audio report.
    const prevRep = this.audioReps.get(this.call.id) ?? null;
    this.audioReps.set(this.call.id, {
      callId: this.call.id, direction: this.call.direction, startedAt: prevRep?.startedAt ?? Date.now(), endedAt: Date.now(),
      codec: a.codec || prevRep?.codec || "", sendCodec: a.sendCodec || prevRep?.sendCodec || "", relay, localType: a.localType || prevRep?.localType || "", network: a.network || prevRep?.network || "",
      jitterMaxMs: Math.max(prevRep?.jitterMaxMs ?? 0, j ?? 0), rttMaxMs: Math.max(prevRep?.rttMaxMs ?? 0, r ?? 0), loss,
      farLostMax: a.farLost != null ? Math.max(prevRep?.farLostMax ?? 0, a.farLost) : prevRep?.farLostMax ?? null,
      concealed: a.concealed - a.silentConcealed, total: a.total, concealEvents: a.events, inserted: a.inserted, removed: a.removed,
      synthSecs: a.synth, playoutSecs: a.playout, samples: (prevRep?.samples ?? 0) + 1,
      // 0 = jitter protection was there but never needed; null = this phone doesn't support it.
      jbTargetMaxMs: Phone.jbSupported() ? Math.max(this.jb.get(this.call.id)?.maxSet ?? 0, prevRep?.jbTargetMaxMs ?? 0) : null,
    });
    const start = this.startReport && this.startReport.callId === this.call.id ? this.startReport : null;
    if (path) saveMediaPath({ ...(path as { ip: string; protocol: string; localType: string; remoteType: string }), region: this.region, rttMs: r, relay, at: Date.now(), bufferMs });
    return { rttMs: r, jitterMs: j, loss, relay, bufferMs, grade, startGapMs: start?.gapMs ?? null, startAudioMs: start?.firstMs ?? null };
  }

  async disconnect() {
    this.stopped = true;
    this.token = null;
    this.ready = false;
    this.reconnectAfterCall = false;
    this.clearReconnectTimer();
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    const old = this.client;
    this.client = null;
    this.waiting = null;
    this.held = null;
    this.monitorExpect = null;
    await old?.disconnect().catch(() => {});
    this.emit();
  }
}
